import React from 'react';
import AuthImage from '../AuthImage';
import {CloudSunIcon, LockIcon} from '../icons';
import {formatDayWeather, TripDay} from '../../service/TripApi';

/** Дата дня в человеческом виде: «пн, 12 окт». */
export const formatDayDate = (date: string): string => {
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
        return date;
    }
    return parsed.toLocaleDateString('ru-RU', {weekday: 'short', day: 'numeric', month: 'short'});
};

interface Props {
    day: TripDay;
    /** Снять фиксацию дня, чтобы перегенерация его пересобрала. */
    onUnlock: (dayId: string) => void;
    busy?: boolean;
}

/**
 * Карточка дня поездки: дата, погода, событие и состав образа.
 *
 * <p>Погода подписывается честно: климатическая оценка помечается словами «оценка по
 * сезону» — прогноза на дату за горизонтом провайдера не существует, и выдавать оценку
 * за факт нельзя.</p>
 */
const TripDayCard: React.FC<Props> = ({day, onUnlock, busy}) => {
    const weatherText = formatDayWeather(day.weather);
    return (
        <li
            data-testid={`trip-day-${day.id}`}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 space-y-3"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-gray-100">{formatDayDate(day.date)}</p>
                    {day.eventLabel && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{day.eventLabel}</p>
                    )}
                    {weatherText && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                            <CloudSunIcon className="w-4 h-4"/>
                            {weatherText}
                        </p>
                    )}
                </div>
                {day.locked && (
                    <button
                        type="button"
                        onClick={() => onUnlock(day.id)}
                        disabled={busy}
                        data-testid={`trip-day-unlock-${day.id}`}
                        title="Снять фиксацию — день пересоберётся при следующей генерации"
                        className="flex items-center gap-1 text-xs min-h-[44px] px-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300"
                    >
                        <LockIcon className="w-4 h-4"/>
                        Зафиксирован
                    </button>
                )}
            </div>

            {day.items.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid={`trip-day-empty-${day.id}`}>
                    Образ не собран: под этот день не нашлось подходящих вещей.
                </p>
            ) : (
                <ul className="flex flex-wrap gap-2">
                    {day.items.map(item => (
                        <li key={item.id} className="flex items-center gap-2 max-w-full">
                            <span className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                                {item.primaryPhotoUrl ? (
                                    <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name ?? ''}
                                               variant="thumbnail" className="w-full h-full object-cover"/>
                                ) : (
                                    <span className="w-full h-full flex items-center justify-center text-gray-400 text-xs">—</span>
                                )}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{item.name}</span>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export default TripDayCard;
